import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { wibToday } from "@/lib/bizdate";
import { useAuth } from "@/context/AuthContext";
import { useMasters } from "@/hooks/useMasters";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { rupiah, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, MessageSquareWarning, Pencil } from "lucide-react";

const STATUSES = ["Baru", "Diproses", "Menunggu Customer", "Selesai", "Ditolak"];
const TYPES = ["Barang Rusak", "Salah Kirim", "Ukuran Tidak Sesuai", "Keterlambatan", "Refund", "Lainnya"];

export default function Complaints() {
  const { can } = useAuth();
  const { masters } = useMasters();
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => { setRows(null); api.get("/complaints").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e))); };
  useEffect(load, []);
  return (
    <div>
      <PageHeader title="Komplain" subtitle="Penanganan komplain pelanggan per toko." testId="complaints-header">
        {can("complaints.manage") && <Button onClick={() => setOpen(true)} data-testid="create-complaint-btn" className="bg-indigo-600 hover:bg-indigo-500"><Plus className="mr-1.5 h-4 w-4" /> Catat Komplain</Button>}
      </PageHeader>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        {!rows ? <Loading /> : rows.length === 0 ? <EmptyState title="Belum ada komplain" icon={MessageSquareWarning} /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>No</TableHead><TableHead>Tanggal</TableHead><TableHead>Toko</TableHead><TableHead>Tipe</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Estimasi Kerugian</TableHead><TableHead>PIC</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {rows.map((c) => (
                  <TableRow key={c.id} data-testid={`complaint-row-${c.complaint_no}`}>
                    <TableCell className="font-mono text-xs">{c.complaint_no}</TableCell><TableCell>{formatDate(c.tanggal)}</TableCell><TableCell>{c.store_name}</TableCell>
                    <TableCell>{c.complaint_type}</TableCell><TableCell className="text-right">{c.quantity}</TableCell><TableCell className="text-right">{rupiah(c.estimated_loss)}</TableCell>
                    <TableCell>{c.pic || "-"}</TableCell><TableCell><StatusBadge status={c.status} /></TableCell>
                    <TableCell>{can("complaints.manage") && <Button variant="ghost" size="sm" onClick={() => setEdit(c)}><Pencil className="h-4 w-4" /></Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      {(open || edit) && <CDialog masters={masters} row={edit} onClose={() => { setOpen(false); setEdit(null); }} onSaved={() => { setOpen(false); setEdit(null); load(); }} />}
    </div>
  );
}

function CDialog({ masters, row, onClose, onSaved }) {
  const today = wibToday();
  const [form, setForm] = useState(row || { tanggal: today, store_id: "", marketplace_id: "", complaint_type: "", quantity: 1, estimated_loss: 0, description: "", solution: "", pic: "", status: "Baru", notes: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.store_id || !form.marketplace_id || !form.complaint_type) return toast.error("Lengkapi toko, marketplace & tipe");
    setSaving(true);
    try {
      const body = { ...form, quantity: Number(form.quantity), estimated_loss: Number(form.estimated_loss) };
      if (row) await api.put(`/complaints/${row.id}`, body); else await api.post("/complaints", body);
      toast.success("Tersimpan"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{row ? "Ubah" : "Catat"} Komplain</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <F label="Tanggal"><Input type="date" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })} /></F>
          <F label="Tipe Komplain"><Select value={form.complaint_type} onValueChange={(v) => setForm({ ...form, complaint_type: v })}><SelectTrigger data-testid="c-type"><SelectValue placeholder="Pilih..." /></SelectTrigger><SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></F>
          <F label="Toko"><S value={form.store_id} onChange={(v) => setForm({ ...form, store_id: v })} options={masters.stores} testId="c-store" /></F>
          <F label="Marketplace"><S value={form.marketplace_id} onChange={(v) => setForm({ ...form, marketplace_id: v })} options={masters.marketplaces} testId="c-marketplace" /></F>
          <F label="Qty"><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></F>
          <F label="Estimasi Kerugian (Rp)"><Input type="number" value={form.estimated_loss} onChange={(e) => setForm({ ...form, estimated_loss: e.target.value })} /></F>
          <F label="PIC"><Input value={form.pic} onChange={(e) => setForm({ ...form, pic: e.target.value })} /></F>
          <F label="Status"><Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid="c-status"><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></F>
        </div>
        <F label="Deskripsi"><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></F>
        <F label="Solusi"><Textarea value={form.solution} onChange={(e) => setForm({ ...form, solution: e.target.value })} /></F>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="save-complaint-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "Menyimpan..." : "Simpan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
const F = ({ label, children }) => <div><Label className="text-xs text-slate-500">{label}</Label><div className="mt-1">{children}</div></div>;
const S = ({ value, onChange, options, testId }) => <Select value={value} onValueChange={onChange}><SelectTrigger data-testid={testId}><SelectValue placeholder="Pilih..." /></SelectTrigger><SelectContent>{options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select>;
